import React from "react";
/* eslint-disable */
import {
  Box, TableHead, TableBody, TableCell, TableContainer, TableRow, Table
} from '@material-ui/core';
import moment from 'moment';

export default class ComponentToPrint extends React.Component {

  getFirstAvailableValue(row, keys, defaultValue = '') {
    if (!row) {
      return defaultValue;
    }
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return defaultValue;
  }

  formatAmount(value) {
    const numberValue = parseFloat(value);
    if (!Number.isFinite(numberValue)) {
      return '-';
    }
    return numberValue.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }

  formatDate(value) {
    if (!value) {
      return '';
    }
    return moment(value).format('YYYY/MM/DD');
  }

  formatUom(value) {
    const uomCode = String(value ?? '').trim();
    const uomMap = {
      '1': 'Unit',
      '2': 'Kg',
      '3': 'g',
      '4': 'Batch'
    };

    return uomMap[uomCode] || uomCode;
  }

  toNumber(value, defaultValue = 0) {
    const numberValue = parseFloat(value);
    return Number.isFinite(numberValue) ? numberValue : defaultValue;
  }

  calculateStockSummary(data) {
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) {
      return null;
    }

    const openingQtyKeys = ['openingQtyInHand', 'openingQty', 'openingStockQty', 'openingStockQuantity', 'openingQuantity', 'openingStock'];
    const qtyInHandKeyCandidates = ['fDCInbound', 'fdcInbound', 'FDCInbound'];
    const unitCostKeys = ['wacPrice', 'wACPrice', 'wac', 'costPrice', 'unitPrice', 'avgCost', 'averageCost'];
    const valueKeys = ['value', 'totalValue', 'stockValue', 'inHandValue', 'qtyInHandValue', 'closingValue'];
    const openingValueKeys = ['openingValue', 'openingStockValue', 'openingStockAmount'];
    const totalQtyKeys = ['totalQty', 'TotalQty'];

    const qtyInHandKey = qtyInHandKeyCandidates.find((key) => rows.some((r) => r && r[key] !== undefined && r[key] !== null && r[key] !== ''));

    const skuName = this.getFirstAvailableValue(rows[0], ['skuName', 'sku', 'productName'], '');
    const skuCode = this.getFirstAvailableValue(rows[0], ['skuCode', 'sku_code'], '');

    let openingQty = 0;
    let qtyInHand = 0;
    let qtyInHandHasAny = false;
    let weightedCostNumerator = 0;
    let weightedCostDenominator = 0;
    let totalValue = 0;
    let totalValueHasAny = false;
    let openingValue = 0;
    let openingValueHasAny = false;
    let totalInvoiceAmount = 0;
    let totalQty = 0;

    rows.forEach((row) => {
      const openingQtyRow = this.toNumber(this.getFirstAvailableValue(row, openingQtyKeys, 0), 0);
      const qtyInHandRaw = qtyInHandKey ? row?.[qtyInHandKey] : null;
      const qtyInHandRow = this.toNumber(qtyInHandRaw, 0);
      const unitCostRow = this.toNumber(this.getFirstAvailableValue(row, unitCostKeys, null), NaN);
      const invoiceAmountRow = this.toNumber(this.getFirstAvailableValue(row, ['invoiceAmount'], 0), 0);
      const totalQtyRow = this.toNumber(this.getFirstAvailableValue(row, totalQtyKeys, 0), 0);

      openingQty += openingQtyRow;
      if (qtyInHandKey) {
        qtyInHand += qtyInHandRow;
        qtyInHandHasAny = true;
      }
      totalInvoiceAmount += invoiceAmountRow;
      totalQty += totalQtyRow;

      if (qtyInHandKey && Number.isFinite(unitCostRow)) {
        weightedCostNumerator += qtyInHandRow * unitCostRow;
        weightedCostDenominator += qtyInHandRow;
      }

      const rowValueRaw = this.getFirstAvailableValue(row, valueKeys, null);
      const rowValue = this.toNumber(rowValueRaw, NaN);
      if (Number.isFinite(rowValue)) {
        totalValue += rowValue;
        totalValueHasAny = true;
      } else if (qtyInHandKey && Number.isFinite(unitCostRow)) {
        totalValue += qtyInHandRow * unitCostRow;
        totalValueHasAny = true;
      }

      const openingValueRaw = this.getFirstAvailableValue(row, openingValueKeys, null);
      const openingValueRow = this.toNumber(openingValueRaw, NaN);
      if (Number.isFinite(openingValueRow)) {
        openingValue += openingValueRow;
        openingValueHasAny = true;
      } else if (Number.isFinite(unitCostRow)) {
        openingValue += openingQtyRow * unitCostRow;
        openingValueHasAny = true;
      }
    });

    let wacPrice = 0;
    if (qtyInHandHasAny && qtyInHand !== 0 && totalValueHasAny) {
      wacPrice = totalValue / qtyInHand;
    } else if (weightedCostDenominator !== 0) {
      wacPrice = weightedCostNumerator / weightedCostDenominator;
    }

    const openingValueComputed = openingValueHasAny ? openingValue : openingQty * wacPrice;

    const qtyInHandComputed = qtyInHandHasAny ? qtyInHand : NaN;

    const costPrice = totalQty !== 0 ? (totalInvoiceAmount / totalQty) : NaN;
    const qtyValueComputed = (qtyInHandHasAny && Number.isFinite(costPrice)) ? (qtyInHand * costPrice) : NaN;

    return {
      skuName,
      skuCode,
      openingQty,
      openingValue: openingValueComputed,
      qtyInHand: qtyInHandComputed,
      qtyValue: qtyValueComputed,
      wacPrice,
      costPrice
    };
  }

  clampNumber(value, min, max) {
    if (!Number.isFinite(value)) {
      return min;
    }
    return Math.min(max, Math.max(min, value));
  }

  buildReportTableLayout(reportData) {
    const rows = Array.isArray(reportData) ? reportData : [];
    const columns = [
      {
        key: 'date',
        header: 'Date',
        constraints: { min: 7, max: 10 },
        getValue: (row) => this.formatDate(this.getFirstAvailableValue(row, ['date'], null))
      },
      {
        key: 'customerCode',
        header: 'Customer Code',
        constraints: { min: 7, max: 10 },
        getValue: (row) => this.getFirstAvailableValue(row, ['customerCode'], '')
      },
      {
        key: 'customerName',
        header: 'Customer Name',
        constraints: { min: 9, max: 16 },
        getValue: (row) => this.getFirstAvailableValue(row, ['customerName'], '')
      },
      {
        key: 'outletCode',
        header: 'Outlet Code',
        constraints: { min: 8, max: 14 },
        getValue: (row) => this.getFirstAvailableValue(row, ['outletCode'], '')
      },
      {
        key: 'locationCode',
        header: 'Location Code',
        constraints: { min: 7, max: 11 },
        getValue: (row) => this.getFirstAvailableValue(row, ['locationCode'], '')
      },
      {
        key: 'outletName',
        header: 'Outlet Name',
        constraints: { min: 8, max: 14 },
        getValue: (row) => this.getFirstAvailableValue(row, ['outletName'], '')
      },
      {
        key: 'skuCode',
        header: 'SKU Code',
        constraints: { min: 8, max: 14 },
        getValue: (row) => this.getFirstAvailableValue(row, ['skuCode'], '')
      },
      {
        key: 'skuName',
        header: 'SKU Name',
        constraints: { min: 9, max: 16 },
        getValue: (row) => this.getFirstAvailableValue(row, ['skuName'], '')
      },
      {
        key: 'uom',
        header: 'UOM',
        constraints: { min: 6, max: 10 },
        getValue: (row) => this.formatUom(this.getFirstAvailableValue(row, ['uom'], ''))
      },
      {
        key: 'orderedQuantity',
        header: 'Ordered Quantity',
        constraints: { min: 8, max: 12 },
        getValue: (row) => this.getFirstAvailableValue(row, ['orderedQuantity'], '')
      }
    ];

    const maxLenByKey = {};
    let overallMaxLen = 0;
    columns.forEach((c) => {
      maxLenByKey[c.key] = (c.header || '').toString().length;
    });

    rows.forEach((row) => {
      columns.forEach((c) => {
        const value = c.getValue(row);
        const str = value === null || value === undefined ? '' : value.toString();
        const len = str.length;
        if (len > maxLenByKey[c.key]) {
          maxLenByKey[c.key] = len;
        }
      });
    });

    columns.forEach((c) => {
      overallMaxLen = Math.max(overallMaxLen, maxLenByKey[c.key] || 0);
    });

    const weights = columns.map((c) => Math.sqrt(Math.max(1, maxLenByKey[c.key] || 1)));
    const weightSum = weights.reduce((acc, w) => acc + w, 0) || 1;

    const initialPercents = columns.map((c, idx) => {
      const raw = (weights[idx] / weightSum) * 100;
      return this.clampNumber(raw, c.constraints.min, c.constraints.max);
    });

    const percents = [...initialPercents];
    const minByIdx = columns.map((c) => c.constraints.min);
    const maxByIdx = columns.map((c) => c.constraints.max);

    const total = () => percents.reduce((acc, p) => acc + p, 0);
    let delta = 100 - total();

    for (let guard = 0; guard < 200 && Math.abs(delta) > 0.01; guard += 1) {
      const adjustable = columns
        .map((_, idx) => idx)
        .filter((idx) => {
          if (delta > 0) return percents[idx] < maxByIdx[idx] - 0.0001;
          return percents[idx] > minByIdx[idx] + 0.0001;
        });

      if (adjustable.length === 0) {
        break;
      }

      const step = delta / adjustable.length;
      adjustable.forEach((idx) => {
        const next = percents[idx] + step;
        percents[idx] = this.clampNumber(next, minByIdx[idx], maxByIdx[idx]);
      });

      delta = 100 - total();
    }

    let fontSize = 9;
    if (overallMaxLen >= 35) {
      fontSize = 8;
    } else if (overallMaxLen <= 18) {
      fontSize = 10;
    }

    return {
      columns,
      columnPercents: percents,
      fontSize
    };
  }

  render() {
    const reportData = this.props.reportData;
    const data = this.props.searchData;
    const totalValues = this.props.totalRowValue;
    const emptyRows = 0;

    const layout = this.buildReportTableLayout(reportData);

    return (
      <div
        className="order-history-report-print"
        style={{ padding: '10px', '--inv-report-font-size': `${layout.fontSize}px` }}
      >
        <style>
          {`
            /* Keep all print styles inside the printable component so ReactToPrint picks them up. */
            @media print {
              @page {
                size: A4 landscape;
                /* Give a bit more room so outer borders are not clipped by printer margins */
                margin: 12mm;
              }

              html,
              body {
                margin: 0 !important;
                padding: 0 !important;
              }

              .order-history-report-print {
                /* Inner safe padding helps avoid border clipping */
                padding: 2mm !important;
              }

              .order-history-report-table {
                width: 100% !important;
                table-layout: fixed !important;
                border-collapse: collapse;
              }

              .order-history-report-print .MuiTableContainer-root {
                overflow: visible !important;
              }

              .order-history-report-cell {
                font-size: var(--inv-report-font-size, 9px) !important;
                padding: 2px 4px !important;
                line-height: 1.2 !important;
                white-space: normal !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }
            }
          `}
        </style>
        <h3><center><u>Customer Outlet Wise Order History Report</u></center></h3>
        <div>&nbsp;</div>

        <div>&nbsp;</div>
        <Box style={{ width: '100%' }}>
          <TableContainer>
            <Table className="order-history-report-table" aria-label="caption table" size="small">
              <colgroup>
                {layout.columns.map((col, idx) => (
                  <col key={col.key} style={{ width: `${layout.columnPercents[idx]}%` }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="left">Date</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="left">Customer Code</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="left">Customer Name</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="left">Outlet Code</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="left">Location Code</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="left">Outlet Name</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="right">SKU Code</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="right">SKU Name</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="right">UOM</TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="right">Ordered Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row, rowIndex) => {
                  return (
                    <TableRow key={rowIndex}>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="left">
                        {this.formatDate(this.getFirstAvailableValue(row, ['date'], null))}
                      </TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="left">{this.getFirstAvailableValue(row, ['customerCode'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="left">{this.getFirstAvailableValue(row, ['customerName'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="left">{this.getFirstAvailableValue(row, ['outletCode'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="left">{this.getFirstAvailableValue(row, ['locationCode'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="left">{this.getFirstAvailableValue(row, ['outletName'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="right">{this.getFirstAvailableValue(row, ['skuCode'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="right">{this.getFirstAvailableValue(row, ['skuName'], '')}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="right">{this.formatUom(this.getFirstAvailableValue(row, ['uom'], ''))}</TableCell>
                      <TableCell className="order-history-report-cell" sx={{ border: "1px solid black" }} align="right">{this.formatAmount(this.getFirstAvailableValue(row, ['orderedQuantity'], ''))}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="right" colSpan={9}>
                    Total Ordered Quantity
                  </TableCell>
                  <TableCell className="order-history-report-cell" sx={{ fontWeight: "bold", border: "1px solid black" }} align="right">
                    {this.formatAmount(totalValues?.totalOrderedQuantity)}
                  </TableCell>
                </TableRow>
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={10} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>
    );
  }
}
